import React, { useState } from 'react';
import { useAddress } from 'hooks';
import { useSelector } from 'react-redux';
import { get } from 'lodash';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import CardActionArea from '@material-ui/core/CardActionArea';
import Icon from '@material-ui/core/Icon';
import { createStyles, Theme, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import classnames from 'classnames';
import moment from 'moment';

import { Appointment, Availability } from '@lib/resources/types';
import { selectCurrentUser, selectLocale, selectCurrentMasterSettings } from '@lib/selectors';

import FormattedSimpleMessage from '../FormattedSimpleMessage';
import Map from '../Map';

import messages from './messages';

const styles = ({ palette: { secondary, common }, spacing: { unit }, breakpoints: { down } }: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      [down('sm')]: {
        flexDirection: 'column',
      },
    },
    dateWrapper: {
      backgroundColor: secondary.main,
      color: common.white,
      width: 100,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      [down('md')]: {
        display: 'none',
      },
    },
    map: {
      width: 185,
      [down('sm')]: {
        height: 150,
        width: '100%',
        justifyContent: 'center',
      },
    },
    mapBellow: {
      width: '100%',
      height: 150,
    },
    content: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'space-between',
      [down('md')]: {
        justifyContent: 'center',
      },
      '&:last-child': {
        padding: `${unit * 2.5}px ${unit * 4.5}px`,
        [down('md')]: {
          padding: unit * 2,
        },
      },
    },
    row: {
      display: 'flex',
      marginBottom: unit,
    },
    rowInset: {
      marginTop: -1 * unit * 0.5,
      marginLeft: unit * 4,
      '&:not(:first-child)': {
        marginTop: -1 * unit * 0.25,
      },
    },
    icon: {
      marginRight: unit * 1.5,
    },
    hideOnPrint: {
      '@media print': {
        display: 'none',
      },
    },
  });

export interface CardAppointmentDetailsProps {
  classes: any;
  className?: string;
  data?: Appointment | Availability;
  showDate?: boolean;
  variantMapBellow?: boolean;
}

const CardAppointmentDetails: React.FC<CardAppointmentDetailsProps> = ({
  classes,
  className,
  showDate,
  data,
  variantMapBellow = false,
  ...other
}) => {
  const locale = useSelector(selectLocale);
  const currentUser = useSelector(selectCurrentUser);
  const currentMasterSettings = useSelector(selectCurrentMasterSettings);
  const qrCodeSettings = get(currentMasterSettings, 'scheduler.confirmationQrcode', { enabled: false, value: '' });
  const [mapOpen, setMapOpen] = useState(false);
  const address = useAddress(data ? data.facility : {});

  if (!data) {
    return <></>;
  }

  const { facility, provider, date } = data;
  const dateObj = moment(date).locale(locale);
  const addressFallback = facility.address;
  const { phone } = facility;
  const hideProviderDetails = get(currentUser, 'settings.scheduler.hideProviderDetails', false);

  return (
    <Card
      className={classnames(className, {
        [classes.root]: !variantMapBellow,
        [classes.hideOnPrint]: qrCodeSettings.enabled,
      })}
      {...other}
    >
      {!variantMapBellow && (
        <>
          {showDate && (
            <div className={classes.dateWrapper}>
              <Typography variant="body1" color="inherit" gutterBottom>
                {dateObj.format('MMM').replace('.', '')}
              </Typography>
              <Typography variant="h3" color="inherit">
                {dateObj.format('D')}
              </Typography>
            </div>
          )}
          {!showDate && <Map className={classes.map} query={address} queryFallback={addressFallback} />}
        </>
      )}
      <CardContent className={classes.content}>
        {variantMapBellow && (
          <div className={classes.row}>
            <Typography paragraph>
              <strong>
                <FormattedSimpleMessage {...messages.detailsTitle} />
              </strong>
            </Typography>
          </div>
        )}
        <div className={classes.row}>
          <Icon className={classes.icon} fontSize="small" color="primary">
            date_range
          </Icon>
          <Typography variant="body1" color="primary">
            {dateObj.format('LLLL')}
          </Typography>
        </div>
        {!hideProviderDetails && (
          <div className={classes.row}>
            <Icon className={classes.icon} fontSize="small" color="primary">
              person
            </Icon>
            <Typography variant="body1" color="primary">
              {provider.name}
            </Typography>
          </div>
        )}
        <div className={classes.row}>
          <Icon className={classes.icon} fontSize="small" color="primary">
            business
          </Icon>
          <Typography variant="body1" color="primary">
            {facility.name}
          </Typography>
        </div>
        <div className={classes.rowInset}>
          <a href={`https://www.google.com/maps/search/${address}`} target="_blank" rel="noopener noreferrer">
            <Typography variant="caption" color="primary">
              {address}
            </Typography>
          </a>
        </div>
        <div className={classes.rowInset}>
          <Typography variant="caption" color="primary">
            {phone}
          </Typography>
        </div>
      </CardContent>
      {variantMapBellow && (
        <>
          {!mapOpen && !showDate && (
            <CardActionArea onClick={() => setMapOpen(true)}>
              <Divider light />
              <Typography variant="caption" color="primary" align="center">
                <FormattedSimpleMessage {...messages.viewMap} />
              </Typography>
            </CardActionArea>
          )}
          <Collapse in={mapOpen}>
            {showDate && (
              <div className={classes.dateWrapper}>
                <Typography variant="body1" color="inherit" gutterBottom>
                  {dateObj.format('MMM').replace('.', '')}
                </Typography>
                <Typography variant="h3" color="inherit">
                  {dateObj.format('D')}
                </Typography>
              </div>
            )}
            {!showDate && (
              <>
                <Map
                  className={classnames(classes.map, { [classes.mapBellow]: variantMapBellow })}
                  query={address}
                  queryFallback={addressFallback}
                  closeMap={() => setMapOpen(false)}
                />
              </>
            )}
          </Collapse>
        </>
      )}
    </Card>
  );
};

export default withStyles(styles)(CardAppointmentDetails);
